import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { PencilIcon, PlusIcon, TrashIcon } from "../../../../assets/ui/icons";
import { useUpdatePrimaryBillingAddressByBillingIdMutation } from "../../../../store/Billing/billingApiSlice";
import { addressLineOf, primaryAddressOf } from "./paymentDetailsFields";

/* Choose which address the invoice goes to. */
export const BillingAddressPicker = ({
  addresses,
  userName,
  refetch,
  onAdd,
  onEdit,
  onDelete,
  onPicked,
  hasAddressDataChanged,
  setHasAddressDataChanged,
}) => {
  const { token } = useSelector((state) => state.auth);
  const [updatePrimaryBillingAddressByBillingId, { reset }] =
    useUpdatePrimaryBillingAddressByBillingIdMutation();

  /* Transcribed: the list re-reads itself when something about it changed. */
  useEffect(() => {
    refetch?.();
  }, [refetch, hasAddressDataChanged]);

  /* Sorted by id, not left in the order the API returned.
   *
   * Making an address primary moves it to the top of the server's list, so a
   * user who opened this twice found the same address in a different place —
   * and since TIP-646 the pick decides the VAT, not just what is printed on
   * the invoice. Sorting by `billing_add_ID` is what keeps a row where it was:
   * ids don't change when `is_primary` does, so neither does the order. Rows
   * are copied before sorting because `addresses` comes from the RTK Query
   * cache, which must not be mutated. */
  const rows = (Array.isArray(addresses) ? [...addresses] : []).sort(
    (a, b) => Number(a?.billing_add_ID) - Number(b?.billing_add_ID)
  );

  /* Which row to show as selected: the one the order is actually priced against, not simply the one carrying `is_primary`. */
  const activeId = primaryAddressOf(addresses)?.billing_add_ID;

  const pick = async (billingAddId) => {
    const result = await updatePrimaryBillingAddressByBillingId({
      data: { is_primary: true },
      id: billingAddId,
      token,
    });
    /* Transcribed: any answer at all closes the dialog and re-reads the list.
       The mutation is not unwrapped, so a rejected request resolves with an
       `error` key — truthy either way, as the live handler treats it. */
    if (result) {
      setHasAddressDataChanged?.(!hasAddressDataChanged);
      onPicked?.();
    }
    reset();
  };

  return (
    <div
      className={cn(
        "w-[min(92vw,560px)] rounded-[var(--radius-modal)] border border-[var(--line)]",
        "bg-white p-5 sm:p-6"
      )}
    >
      <h2 className="m-0 text-[17px] font-bold text-[var(--ink)] font-sans">
        Change billing address
      </h2>
      <p className="m-0 mt-1 text-[12.5px] text-[var(--muted)] font-sans">
        The one you pick becomes your primary address and receives the invoice.
      </p>

      <fieldset className="m-0 mt-4 flex max-h-[46vh] flex-col gap-2.5 overflow-y-auto border-0 p-0">
        <legend className="sr-only">Billing address</legend>

        {rows.length === 0 && (
          <p className="m-0 text-[13px] text-[var(--muted)] font-sans">
            You have no saved addresses yet.
          </p>
        )}

        {rows.map((row) => (
          <div
            key={row?.billing_add_ID}
            className={cn(
              "flex items-start gap-3 rounded-[12px] border p-3.5 transition-colors",
              row?.billing_add_ID === activeId
                ? "border-[var(--coral)] bg-[var(--coral-soft)]"
                : "border-[var(--line)] bg-white hover:border-[var(--line-2)]"
            )}
          >
            <input
              type="radio"
              name="u1-billing-address"
              className="mt-1 h-4 w-4 shrink-0 accent-[var(--coral)]"
              checked={row?.billing_add_ID === activeId}
              onChange={() => pick(row?.billing_add_ID)}
              aria-label={`Use ${row?.companyName || userName || "this address"}`}
            />

            {/* `grow`, not `flex-1`. tailwind.config.js sets `flex` directly under `theme` instead of `theme.extend`, which REPLACES Tailwind's flex scale: this project... */}
            <div className="min-w-0 grow">
              <p className="m-0 text-[13.5px] font-bold text-[var(--ink)] font-sans">
                {row?.companyName || userName}
              </p>
              {row?.mobile && (
                <p className="m-0 mt-0.5 text-[12.5px] text-[var(--muted)] font-sans">
                  {row.mobile}
                </p>
              )}
              <p className="m-0 mt-0.5 text-[12.5px] leading-[1.5] text-[var(--ink-2)] font-sans">
                {addressLineOf(row)}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                aria-label="Edit address"
                onClick={() => onEdit?.(row?.billing_add_ID)}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)]",
                  "bg-white text-[var(--muted)] transition-colors hover:text-[var(--ink)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)]"
                )}
              >
                <PencilIcon size={13} />
              </button>
              <button
                type="button"
                aria-label="Delete address"
                onClick={() => onDelete?.(row?.billing_add_ID)}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)]",
                  "bg-white text-[var(--muted)] transition-colors hover:text-[var(--coral-deep)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)]"
                )}
              >
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </fieldset>

      <Button variant="coralSolid" className="mt-4 w-full gap-2" onClick={onAdd}>
        <PlusIcon size={14} />
        Add new address
      </Button>
    </div>
  );
};

export default BillingAddressPicker;
